import classNames from 'classnames';
import { Tab, TabList } from '@headlessui/react';
import { Tooltip, DateRangePicker } from '@crazyegginc/hatch';

import { useHasFeatureFlag, useAuthContext } from '/src/hooks';
import { useErrorsFilter } from '../../errors-filter-context';
import { SearchInput } from '/src/components/search-input';
import { ActionIconButton } from '/src/components/ActionIconButton';
import { TIMEZONES } from '/src/features/_global/constants';

import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { ReactComponent as DownloadIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-download.svg';

export function ErrorsFilterBar({ site, pickerProps }) {
  const isHighVolume = useHasFeatureFlag('high-volume-errors-dashboard');
  const { queryParams, setDateRange, setSearch } = useErrorsFilter();
  const { token } = useAuthContext();

  return (
    <div className="mb-10 flex w-full items-end">
      <TabList className="flex border-b border-mystic-500">
        {[
          { title: 'Unresolved errors', tooltip: 'Errors that are still active on your site' },
          { title: 'Resolved errors', tooltip: 'Errors that have been fixed' },
          { title: 'Muted errors', tooltip: 'Active or fixed errors that you want to ignore' },
        ].map((header) => (
          <Tab
            key={header.title}
            className={({ selected }) =>
              classNames(
                'text-header-4 border-b-2 px-5 pb-3 text-dodger-blue-500 focus:outline-none focus-visible:outline-black',
                'flex items-center',
                {
                  'border-dodger-blue-500': selected,
                  'border-transparent': !selected,
                },
              )
            }
            data-testid={header.id}
          >
            {header.title}
            <Tooltip tooltipContent={<div className="w-28">{header.tooltip}</div>}>
              <InfoIcon
                className="ml-1.25 h-3.5 w-3.5 fill-current text-dodger-blue-300"
                aria-label={`Info on ${header.title.toLowerCase()}`}
              />
            </Tooltip>
          </Tab>
        ))}
      </TabList>

      <div className="flex-grow"></div>
      {!isHighVolume ? (
        <div className="mr-2.5 flex items-center">
          <ActionIconButton
            tooltip="Download error list"
            label="Download error list"
            component="a"
            href={`${window.CORE_API_URL}/export/errors.csv?site_id=${site.id}&token=${token}`}
            icon={DownloadIcon}
            download={true}
            className="mr-2 h-[40px] w-[40px]"
          />
          <SearchInput
            search={setSearch}
            placeholder="Search for URL or error"
            initialValue={queryParams.search}
            className="!border-solitude-500 !bg-solitude-500 !font-semibold !placeholder-dodger-blue-500/50"
          />
        </div>
      ) : null}
      <DateRangePicker
        lowerBoundary={pickerProps.lowerBoundary}
        upperBoundary={pickerProps.upperBoundary}
        startDate={pickerProps.startDate}
        endDate={pickerProps.endDate}
        special={pickerProps.special}
        setDateRange={setDateRange}
        setCommonDateRange={setDateRange}
        timezone={TIMEZONES.UTC}
        showCommonRanges={true}
        maxRange={pickerProps.maxRange}
        size="lg"
        showTimeZone={true}
      />
    </div>
  );
}
